import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * When onSearchSubmit is provided: uses local state for the input value so that
 * typing does not trigger parent re-renders. Only syncs to parent on Enter (submit)
 * or when parent clears the value (value === ''). This avoids input lag on heavy pages.
 * When onSearchSubmit is not provided: fully controlled (value + onChange on every keystroke).
 */
const SearchBar = ({ value, onChange, onKeyPress, onSearchSubmit, placeholder = "Search...", autocomplete, uniqueId = "default" }) => {
  const [localValue, setLocalValue] = useState(value ?? '');

  // Sync from parent when parent explicitly clears (e.g. filter reset)
  useEffect(() => {
    if (value === '') {
      setLocalValue('');
    }
  }, [value]);

  const isSubmitMode = typeof onSearchSubmit === 'function';

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (isSubmitMode) {
        onSearchSubmit(localValue);
      }
      onKeyPress?.(e);
    }
  };

  return (
    <div className="input-group input-group-flat w-auto">
      <span className="input-group-text">
        <svg xmlns="http://www.w3.org/2000/svg" width={24} height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="icon icon-1" aria-hidden="true">
          <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
          <path d="M21 21l-6 -6" />
        </svg>
      </span>
      <input
        type="text"
        className="form-control"
        id={`search-input-${uniqueId}`}
        name="search"
        placeholder={placeholder}
        value={isSubmitMode ? localValue : value}
        onChange={(e) => {
          const next = e.target.value;
          if (isSubmitMode) {
            setLocalValue(next);
          } else {
            onChange(next);
          }
        }}
        onKeyDown={handleKeyDown}
        autoComplete={autocomplete}
      />
    </div>
  );
};

SearchBar.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func,
  onSearchSubmit: PropTypes.func,
  placeholder: PropTypes.string,
  autocomplete: PropTypes.string,
  uniqueId: PropTypes.string
};

export default SearchBar; 